import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../hooks/useUserData";
import { db } from "../services/firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import NavbarMobile from "../components/NavbarMobile";
import BottomNav from "../components/BottomNav";
import { Laptop, Clock, CheckCircle, XCircle, Undo } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [user, userLoading] = useUserData();

  // Redirect if profile not complete
  useEffect(() => {
    if (!userLoading && user?.role === "student" && !user?.profileCompleted) {
      navigate("/student/profile-setup");
    }
  }, [user, userLoading, navigate]);

  // Fetch user's requests
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, "requests"), where("requesterId", "==", user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reqs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRequests(reqs);
    });

    return unsubscribe;
  }, [user]);

  const totalRequests = requests.length;
  const approvedRequests = requests.filter((r) => r.status === "approved").length;
  const pendingRequests = requests.filter((r) => r.status === "pending").length;
  const rejectedRequests = requests.filter((r) => r.status === "rejected").length;

  // Show loading state
  if (userLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <div className="skeleton-avatar mb-4"></div>
        <div className="skeleton-title w-48 mb-2"></div>
        <div className="skeleton-text w-32"></div>
      </div>
    );
  }

  // Show empty state if no user
  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <div className="empty-state">
          <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Laptop className="w-8 h-8 text-neutral-400" />
          </div>
          <h3 className="empty-state-title">Access Required</h3>
          <p className="empty-state-description mb-6">Please log in to access your dashboard</p>
          <button
            onClick={() => navigate("/login")}
            className="btn-primary px-6 py-3"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const displayName = user?.name || user?.email?.split("@")[0] || "Student";

  return (
    <div className="min-h-screen bg-neutral-50 animate-fade-in">

      <div className="w-full px-4 pt-20 space-y-6 lg:px-8 xl:px-12">
        {/* Welcome Section */}
        <div className="text-center lg:text-left">
          <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 lg:hidden">
            <Laptop className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Welcome back, {displayName}!
          </h1>
          <p className="text-neutral-600">
            Here's your asset management dashboard
          </p>
        </div>

        {/* Summary Cards */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Your Requests</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Laptop className="w-6 h-6" />}
              title="Total"
              value={totalRequests}
              color="info"
            />
            <StatCard
              icon={<CheckCircle className="w-6 h-6" />}
              title="Approved"
              value={approvedRequests}
              color="success"
            />
            <StatCard
              icon={<Clock className="w-6 h-6" />}
              title="Pending"
              value={pendingRequests}
              color="warning"
            />
            <StatCard
              icon={<XCircle className="w-6 h-6" />}
              title="Rejected"
              value={rejectedRequests}
              color="error"
            />
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ActionButton
              icon={<Laptop className="w-6 h-6" />}
              label="Browse Assets"
              description="View all available assets and submit requests"
              color="primary"
              onClick={() => navigate("/student/browse")}
            />
            <ActionButton
              icon={<Clock className="w-6 h-6" />}
              label="New Request"
              description="Borrow an asset or report a maintenance issue"
              color="warning"
              onClick={() => navigate("/student/item-request")}
            />
            <ActionButton
              icon={<Undo className="w-6 h-6" />}
              label="Return Asset"
              description="Scan QR code to return a borrowed asset"
              color="info"
              onClick={() => navigate("/student/return-scan")}
            />
            <ActionButton
              icon={<CheckCircle className="w-6 h-6" />}
              label="My Requests"
              description="Track your submitted requests"
              color="success"
              onClick={() => navigate("/student/requests")}
            />
          </div>
        </section>
      </div>

    </div>
  );
}

// Helper Components
function StatCard({ icon, title, value, color }) {
  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return 'bg-primary-500 text-white';
      case 'success': return 'bg-success-500 text-white';
      case 'warning': return 'bg-warning-500 text-white';
      case 'error': return 'bg-error-500 text-white';
      case 'info': return 'bg-info-500 text-white';
      default: return 'bg-neutral-500 text-white';
    }
  };

  return (
    <div className="card-interactive p-6 text-center animate-fade-in">
      <div className={`w-12 h-12 rounded-2xl ${getColorClasses(color)} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-neutral-900 mb-1">{value}</div>
      <div className="text-sm font-medium text-neutral-700">{title}</div>
    </div>
  );
}

function ActionButton({ icon, label, description, color, onClick }) {
  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return 'bg-primary-500 hover:bg-primary-600 text-white';
      case 'success': return 'bg-success-500 hover:bg-success-600 text-white';
      case 'warning': return 'bg-warning-500 hover:bg-warning-600 text-white';
      case 'error': return 'bg-error-500 hover:bg-error-600 text-white';
      case 'info': return 'bg-info-500 hover:bg-info-600 text-white';
      default: return 'bg-neutral-500 hover:bg-neutral-600 text-white';
    }
  };

  return (
    <button
      onClick={onClick}
      className={`card-interactive p-6 lg:p-4 text-left transition-all duration-200 ${getColorClasses(color)} animate-fade-in`}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1">{label}</h3>
          <p className="text-sm opacity-90">{description}</p>
        </div>
      </div>
    </button>
  );
}
